/* @flow */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Row, Col } from 'react-grid-system';
import {
  Button,
  Checkbox,
  Images,
  InputClearable,
  LoadingIndicator,
  Modal,
  ModalTitle,
  PromptText,
} from 'listertech-commons-web';
import { callGetAgencies, callEnrollMany } from '../../api/agencies';
//import { callGetOne } from '../../api/users';
import { callUpdateContractor, callGetOne } from '../../api/users';

const ModalButtons = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  min-width: 400px;
  margin-top: 40px;

  > * {
    width: auto;
    padding: 0px 20px;
  }
`;

const Title = styled(ModalTitle)`
  display: inline-block;
`;

const TitleExtra = styled.p`
  display: inline-block;
  margin: 0;
  font-family: Montserrat;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  color: #9da8ae;
`;

const Label = styled(TitleExtra)`
  font-size: 16px;
`;

const Icon = styled.img`
  width: 18px;
  height: 18px;
  margin-left: 12px;
`;

const IconLabel = styled(Label)`
  margin-left: 12px;
`;

const List = styled.div`
  margin-top: 12px;
  max-height: 300px;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const ListItem = styled.div`
  display: flex;
  margin-top: 12px;
`;

const AutoAcceptModal = props => {
  const { contractor, onClose } = props;

  const [allowedAutoAccept, setAllowedAutoAccept] = useState(contractor?.autoAccept?.allowed || false);
  const [updating, setUpdating] = useState(false);
  const [tier, setTier] = useState(contractor?.tier || 2);

  useEffect(() => {
    if (contractor && contractor._id) {
      fetchContractorDetails();
    }
  }, [contractor]);
  

  const fetchContractorDetails = async () => {
    try {
        const result = await callGetOne(contractor._id);
        console.log('Fetched contractor:', result);
        if (result && result.autoAccept) {
          setAllowedAutoAccept(result.autoAccept.allowed);
          setTier(result.tier || 2);
        } else {
          console.warn('autoAccept not found in contractor data', result);
          setAllowedAutoAccept(false);
          setTier(2);
        }
    } catch (error) {
        console.error('Failed to fetch contractor details', error);
        setAllowedAutoAccept(false); // fallback
        setTier(2);
    }
  };

  const handleToggleAutoAccept = async () => {
    setUpdating(true);
    try {
      await callUpdateContractor(contractor._id, {'autoAccept.allowed': !allowedAutoAccept,});

      setAllowedAutoAccept(prev => !prev);
  
    } catch (err) {
      alert("Error updating auto-accept status");
    } finally {
      setUpdating(false);
    }
  };

  const handleTierChange = async (value) => {
    setUpdating(true);
    try {
      const numericValue = parseInt(value, 10);
      await callUpdateContractor(contractor._id, { tier: numericValue });
      setTier(numericValue);
    } catch (err) {
      alert("Error updating tier");
    } finally {
      setUpdating(false);
    }
  };

const VisitScheduleModal = ({ autoAcceptSchedule }) => {
    const visitsArray = autoAcceptSchedule?.schedule?.current || [];

    const getDateLabel = (index) => {
      const date = new Date();
      date.setDate(date.getDate() + index);
      if (index === 0) return "Today";
      return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
    };

    const getStyles = (count) => {
      if (count === 0) return { bg: '#f9fafb', text: '#9ca3af', border: '#e5e7eb' };
      if (count < 3) return { bg: '#e0f2fe', text: '#0284c7', border: '#bae6fd' };
      return { bg: '#0ea5e9', text: '#ffffff', border: '#0284c7' };
    };

    // If no data, show a simple loading or empty state
    if (visitsArray.length === 0) {
      return <PromptText>No schedule data available.</PromptText>;
    }

    return (
      <div style={{ width: '100%', marginTop: '20px' }}>
        <Title style={{ fontSize: '20px', marginBottom: '10px', color: '#000000'}}>
          Accepted Visits (14 Days)
        </Title>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '8px',
        }}>
          {visitsArray.map((count, index) => {
            const styles = getStyles(count);
            return (
              <div key={index} style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: '8px 4px', borderRadius: '6px', backgroundColor: styles.bg,
                border: `1px solid ${styles.border}`
              }}>
                <span style={{ fontSize: '0.85rem', fontFamily: 'Montserrat', color: count > 2 ? '#fff' : '#6b7280' }}>
                  {getDateLabel(index)}
                </span>
                <span style={{ fontSize: '1.1rem', fontFamily: 'Montserrat', color: styles.text }}>
                  {count}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Modal visible>
      <Row justify="space-between" align="start">
        <Col xs={12}>
          <Title>Current Auto-Accept Schedule</Title>
          <VisitScheduleModal autoAcceptSchedule={contractor?.autoAccept} />
        </Col>
      </Row>
      <Row justify="space-between" align="center" style={{ marginTop: '20px' }}>
        <Title>Allow Auto-Accept</Title>
        <TitleExtra>{contractor && contractor.name}</TitleExtra>
      </Row>
      <ModalButtons style={{ justifyContent: 'center' }}>
        <Button
            onClick={handleToggleAutoAccept}
            loading={updating}
            noMargin
            style={{ minWidth: '200px' }}
        >
            {allowedAutoAccept ? 'Disable Auto-Accept' : 'Enable Auto-Accept'}
        </Button>
        </ModalButtons>
        {allowedAutoAccept && (
        <Row style={{ marginTop: '20px', justifyContent: 'center' }}>
          <Label style={{ marginRight: '10px', marginLeft: '15px', fontSize: '18px', marginTop: '10px'}}>Tier</Label>
          <select
            value={tier}
            onChange={e => handleTierChange(e.target.value)}
            disabled={updating}
            style={{ padding: '8px', fontSize: '14px', borderRadius: '4px' }}
            >
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
          </select>
        </Row>
      )}
        <ModalButtons>
                <Button noMargin theme="light" onClick={onClose}>
                  Exit
                </Button>
        </ModalButtons>
    </Modal>
  );
};

export default AutoAcceptModal;
